import { TaskItemAggregate, TaskItemViewProps } from '../item';
import { TaskListController } from './taskListInterface';
import { ReportingShellControllerBaseConfig } from '../../../base';
import { assertNever } from '@varos/util-typescript';
import { AiOutlineCheck, AiOutlineWarning } from 'react-icons/ai';
import { capitalize } from 'lodash';
import pluralize from 'pluralize';

export function createTaskListController(
    config: ReportingShellControllerBaseConfig
): TaskListController {
    const {
        kernel: {
            infra: { formatter },
        },
    } = config;

    function getStatus(item: TaskItemAggregate): TaskItemViewProps['status'] {
        if (item.task.status === 'pending') {
            return {
                isLoading: true,
                label: 'Pending',
                colorScheme: 'blue',
                Icon: null,
                description: 'Task is pending and not yet started',
            };
        }
        if (item.task.status === 'running') {
            return {
                isLoading: true,
                label: 'Running',
                colorScheme: 'orange',
                Icon: null,
                description: 'Task is running',
            };
        }
        if (item.task.status === 'completed') {
            return {
                isLoading: false,
                label: 'Completed',
                description: 'Task is completed',
                colorScheme: 'green',
                Icon: AiOutlineCheck,
            };
        }
        if (item.task.status === 'failed') {
            return {
                isLoading: false,
                label: 'Failed',
                description: item.task.error ?? 'Task failed',
                colorScheme: 'red',
                Icon: AiOutlineWarning,
            };
        }
        assertNever(item.task.status);
    }

    return {
        useProps(context, item, props) {
            const runningCount = item.items.filter(
                (item) => item.task.status === 'running'
            ).length;
            return {
                status: {
                    isLoading: runningCount > 0,
                    label:
                        runningCount > 0
                            ? `${runningCount} ${pluralize('task', runningCount)} running`
                            : 'No pending tasks',
                },
                items: item.items.map(
                    (item): TaskItemViewProps => ({
                        id: item.task.id,
                        label: item.task.id,
                        status: getStatus(item),
                        timestamp: {
                            relative: formatter.timeago(item.task.created_at, {
                                notation: 'short',
                            }),
                            absolute: formatter.date(item.task.created_at),
                        },
                    })
                ),
            };
        },
    };
}
